import { getAccountNumberError, getRoutingNumberError } from '@steerfi/shared';
import type { PaymentMethodOnFile } from '@/api/suppliers';

export type PaymentMethodType = 'ACH' | 'CHECK' | 'DIGITAL_WALLET';

/**
 * A method is "returned" (retained + flagged) once it carries any review state —
 * NEEDS_REVIEW while pending, or CLEARED after a replacement was added. Keyed off
 * review_status (not return_code) so check/digital-wallet returns, which carry no
 * ACH return code, are still recognized.
 */
export function isMethodReturned(method: Pick<PaymentMethodOnFile, 'review_status'>): boolean {
  return method.review_status != null;
}

/** True when any method has a review state (drives the Payment Methods on File section). */
export function hasReturnedMethod(methods: Pick<PaymentMethodOnFile, 'review_status'>[]): boolean {
  return methods.some(isMethodReturned);
}

/**
 * The method that still needs review — drives the banner + Update action.
 * Null once every returned method has been resolved (CLEARED).
 */
export function findReturnedMethod(methods: PaymentMethodOnFile[]): PaymentMethodOnFile | null {
  return methods.find((m) => m.review_status === 'NEEDS_REVIEW') ?? null;
}

/** Method types CASS can disburse, and therefore reissue a returned payment on. */
const REISSUABLE_METHOD_TYPES: ReadonlySet<string> = new Set(['ACH', 'CHECK']);

/**
 * True when a return happened but no method CASS can reissue on has been added
 * yet. A Digital Wallet replacement does not resolve the return — DW is issued
 * by Dash, not CASS — so the Update action stays available until an ACH or
 * Check method is on file.
 */
export function needsReissuableMethod(methods: PaymentMethodOnFile[]): boolean {
  if (!hasReturnedMethod(methods)) return false;
  return !methods.some((m) => !isMethodReturned(m) && REISSUABLE_METHOD_TYPES.has(m.type));
}

/**
 * The method the modal shows as "old method (for verification)": the one that
 * caused the return. Prefer an unresolved (NEEDS_REVIEW) method, else the
 * retained returned method (CLEARED). Never the freshly-added replacement.
 */
export function findMethodToReplace(methods: PaymentMethodOnFile[]): PaymentMethodOnFile | null {
  return (
    methods.find((m) => m.review_status === 'NEEDS_REVIEW') ??
    methods.find((m) => m.review_status != null) ??
    null
  );
}

/**
 * A returned check or digital wallet cannot be replaced with the same method
 * type (nothing differentiates it). ACH is exempt — differing account details
 * make it a distinct method (enforced server-side on the decrypted values).
 */
export function getBlockedReplacementType(
  returnedMethod: Pick<PaymentMethodOnFile, 'type'> | null
): PaymentMethodType | null {
  if (returnedMethod?.type === 'CHECK') return 'CHECK';
  if (returnedMethod?.type === 'DIGITAL_WALLET') return 'DIGITAL_WALLET';
  return null;
}

/**
 * Client-side validation for the replacement form. Returns the first error
 * message, or null when the entry is acceptable. The ACH exact-duplicate check
 * is enforced server-side (it needs the decrypted old account number).
 */
export function getReplacementValidationError(input: {
  methodType: PaymentMethodType;
  routingNumber: string;
  accountNumber: string;
  blockedType: PaymentMethodType | null;
}): string | null {
  const { methodType, routingNumber, accountNumber, blockedType } = input;
  if (blockedType && methodType === blockedType) {
    return methodType === 'CHECK'
      ? 'A returned check cannot be replaced with another check. Choose a different payment method.'
      : 'A returned digital wallet cannot be replaced with another digital wallet. Choose a different payment method.';
  }
  if (methodType !== 'ACH') return null;
  return getRoutingNumberError(routingNumber) ?? getAccountNumberError(accountNumber) ?? null;
}
